import React from 'react';
import { useRequestRevisionsStore } from 'src/features/revisions/use-revisions-context-provider';
import { MinimalMediaCard } from 'src/components/MediaCard/minimal-media-card';
import { cn } from 'src/lib/utils';
import { Separator } from 'src/components/ui/separator';

type Props = React.PropsWithChildren<React.ComponentPropsWithoutRef<'div'>>;

const RevisionMediaList: React.FC<Props> = ({ children, ...props }) => {
  const { setSelectedRevisionMedia, selectedRevisionMedia, selectedRevision } =
    useRequestRevisionsStore();

  // Define media groups in order
  const mediaGroups = [
    { name: 'default', media: selectedRevision?.media.filter((m) => m.collection_name === 'default') ?? [] },
    { name: 'resizes', media: selectedRevision?.media.filter((m) => m.collection_name === 'resizes') ?? [] },
    { name: 'thumbnails', media: selectedRevision?.media.filter((m) => m.collection_name === 'thumbnails') ?? [] },
    { name: 'captions', media: selectedRevision?.media.filter((m) => m.collection_name === 'captions') ?? [] },
    { name: 'project_files', media: selectedRevision?.media.filter((m) => m.collection_name === 'project_files') ?? [] },
    { name: 'transcript', media: selectedRevision?.media.filter((m) => m.collection_name === 'transcript') ?? [] },
  ];

  // Render a media group with optional separator
  const renderMediaGroup = (media: typeof mediaGroups[0]['media'], isFirst: boolean = false) => {
    if (!media.length) return null;

    return (
      <>
        {!isFirst && (
          <>
            <Separator className={'tw-my-0 tw-hidden xl:tw-block'} />
            <Separator orientation={'vertical'} className={'tw-mx-0 xl:tw-hidden'} />
          </>
        )}
        {media.map((m) => (
          <div
            key={m?.id}
            className={cn('tw-cursor-pointer tw-p-2', {
              'tw-rounded': selectedRevisionMedia?.id === m?.id,
            })}
            style={{
              backgroundColor: selectedRevisionMedia?.id === m?.id ? '#E8F1FF' : undefined,
              border:
                selectedRevisionMedia?.id === m?.id
                  ? '1px solid #2779C6'
                  : '1px solid transparent',
              transition: 'all 0.25s ease-in-out',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRevisionMedia(m?.id);
            }}
          >
            <MinimalMediaCard
              showTitle
              canView={false}
              media={m!}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      {...props}
      className={cn(
        'tw-flex tw-h-36 tw-min-w-28 tw-rounded xl:tw-h-auto xl:tw-flex-col',  // Match admin: tw-min-w-28 instead of tw-w-32
        props.className,
      )}
    >
      <div
        className={
          'tw-relative tw-flex tw-w-full tw-flex-1 tw-gap-3 tw-rounded xl:tw-w-28 xl:tw-flex-col'  // Match admin container structure
        }
      >
        <div
          className={
            'tw-absolute tw-inset-0 tw-flex tw-items-end tw-gap-2 tw-overflow-x-auto tw-px-2 tw-py-2 xl:tw-flex-col xl:tw-items-center xl:tw-overflow-y-auto xl:tw-px-0'  // Match admin scrolling classes
          }
        >
          {/* Render all media groups */}
          {mediaGroups.map((group, index) => renderMediaGroup(group.media, index === 0))}
        </div>
      </div>
      {children}
    </div>
  );
};

export type { Props as RevisionMediaListProps };
export { RevisionMediaList };
